import type { ReactNode } from "react";
import "./ScheduleAlertsDialog.scss";
import CustomDialog from "@/shared/components/generic/custom-dialog/CustomDialog.tsx";
import IconMagnifierCheck from "@/shared/components/generic/icons/IconMagnifierCheck.tsx";
import { useTranslation } from "react-i18next";
import DialogActionsButtons from "@/shared/components/generic/dialog-actions-buttons/DialogActionsButtons.tsx";
import Button from "@/shared/components/generic/button/Button.tsx";
import { Level, Size } from "@/shared/components/generic/UiEnums.ts";
import useValidateSchedules from "@/pages/human-resources/time-management/services/hooks/useValidateSchedules.ts";
import ScheduleAlertsList from "@/pages/human-resources/time-management/components/schedule-alerts/alerts-list/ScheduleAlertsList.tsx";
import useScheduleViewState from "@/pages/human-resources/time-management/model/schedule-view/useScheduleViewState.ts";
import useScheduleViewStateDispatch from "@/pages/human-resources/time-management/model/schedule-view/useScheduleViewStateDispatch.ts";
import type { ScheduleAlerts } from "@/pages/human-resources/time-management/business/schedule/ScheduleAlerts.ts";
import ThreeDots from "@/shared/components/generic/loading/ThreeDots.tsx";
import useVisibleDraftSchedules, {
  filterDraftSchedules,
} from "@/pages/human-resources/time-management/model/schedule-view/useVisibleDraftSchedules.ts";
import FilteredOutWarning from "@/pages/human-resources/time-management/components/schedule-alerts/FilteredOutWarning.tsx";
import useScheduleUserPermissions from "@/pages/human-resources/time-management/hooks/useScheduleUserPermissions.ts";

export function ScheduleAlertsDialog(): ReactNode {
  const { t } = useTranslation(["common", "humanResources"]);
  const {
    alertsDialogState: { alerts, verifiedSchedules },
  } = useScheduleViewState();
  const { callback } = useScheduleViewStateDispatch();
  const validateSchedules = useValidateSchedules();
  const { canValidate } = useScheduleUserPermissions();
  const verifiedDrafts = filterDraftSchedules(verifiedSchedules);
  const visibleDrafts = useVisibleDraftSchedules();

  const onClose = callback({ type: "closeAlertsDialog" });
  return (
    <CustomDialog
      open={true}
      onClose={onClose}
      icon={IconMagnifierCheck}
      title={t("humanResources:timeManagement.scheduleAlerts.verifySchedule")}
      hideBackdrop={true}
      dontCloseOnBackdropClick={true}
    >
      <div className="schedule-alerts-dialog">
        {alerts === undefined ?
          <ThreeDots />
        : renderContent(alerts)}
      </div>
    </CustomDialog>
  );

  function renderContent(alerts: ScheduleAlerts): ReactNode {
    return (
      <div className="content">
        <FilteredOutWarning />
        <ScheduleAlertsList
          blockingAlerts={alerts.getBlockings()}
          warningAlerts={alerts.getWarnings()}
        />
        <DialogActionsButtons>
          {visibleDrafts.length > 0 && canValidate ?
            renderCancelValidateButtons(alerts)
          : renderCloseButton()}
        </DialogActionsButtons>
      </div>
    );
  }

  function renderCancelValidateButtons(alerts: ScheduleAlerts): ReactNode {
    return (
      <>
        <Button
          onClick={onClose}
          label={t("common:actions.cancel")}
          size={Size.SMALL}
          level={Level.SECONDARY}
        />
        <Button
          disabled={alerts.hasBlocking()}
          onClick={() => {
            validateSchedules.mutate(verifiedDrafts);
            onClose();
          }}
          label={t(
            "humanResources:timeManagement.scheduleAlerts.validateSchedule",
          )}
          size={Size.SMALL}
          level={Level.PRIMARY}
        />
      </>
    );
  }
  function renderCloseButton(): ReactNode {
    return (
      <Button
        onClick={onClose}
        label={t("common:actions.close")}
        size={Size.SMALL}
        level={Level.PRIMARY}
      />
    );
  }
}
